import { useTranslation } from 'react-i18next';
import { BarChart3, Shield, Brain, CheckCircle2, AlertTriangle, Info, Target } from 'lucide-react';
import CollapsibleSection from './CollapsibleSection';

function ScoreBar({ label, score, weight }) {
  const color = score >= 70 ? '#45C04A' : score >= 50 ? '#F59E0B' : '#EF4444';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-500 dark:text-slate-400">{label}</span>
        <span className="font-semibold tabular-nums" style={{ color }}>{score} <span className="text-slate-400 font-normal">({weight}%)</span></span>
      </div>
      <div className="h-1.5 bg-slate-100 dark:bg-white/[0.06] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function AiInsights({ aiData }) {
  const { t } = useTranslation();
  const scores = aiData.scores || {};
  const ai = aiData.ai_analysis || {};

  return (
    <div data-testid="ai-insights" className="space-y-3">
      <CollapsibleSection title={t('decision.financialAnalysis')} icon={BarChart3} testId="section-financial">
        <div className="space-y-3">
          <ScoreBar label={t('aiEngine.financialScore')} score={scores.financial_score || 0} weight={35} />
          <ScoreBar label={t('aiEngine.locationScore')} score={scores.location_score || 0} weight={20} />
          <ScoreBar label={t('aiEngine.marketScore')} score={scores.market_score || 0} weight={20} />
          <ScoreBar label={t('aiEngine.propertyScore')} score={scores.property_score || 0} weight={15} />
          <ScoreBar label={t('aiEngine.riskScore')} score={scores.risk_score || 0} weight={10} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title={t('decision.riskAnalysis')} icon={Shield} testId="section-risk">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-500 mb-2">{t('aiEngine.strengths')}</p>
            {(aiData.strengths || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mb-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                {t(`aiEngine.str_${s}`, s.replace(/_/g, ' '))}
              </div>
            ))}
            {!(aiData.strengths || []).length && <p className="text-xs text-slate-400">{t('aiEngine.noStrengths')}</p>}
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-red-500 mb-2">{t('aiEngine.risks')}</p>
            {(aiData.risks || []).map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mb-1.5">
                <AlertTriangle className="w-3 h-3 text-red-400 flex-shrink-0" />
                {t(`aiEngine.risk_${r}`, r.replace(/_/g, ' '))}
              </div>
            ))}
            {!(aiData.risks || []).length && <p className="text-xs text-slate-400">{t('aiEngine.noRisks')}</p>}
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title={t('decision.fullAiReasoning')} icon={Brain} testId="section-ai-reasoning">
        <div className="space-y-3">
          {ai.summary && <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{ai.summary}</p>}
          {ai.detailed_reasoning && (
            <div className="bg-slate-50 dark:bg-white/[0.03] rounded-lg p-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{t('aiEngine.reasoning')}</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">{ai.detailed_reasoning}</p>
            </div>
          )}
          {ai.recommendation_explanation && (
            <p className="text-sm text-slate-600 dark:text-slate-300 italic border-l-2 border-slate-200 dark:border-white/10 pl-3">{ai.recommendation_explanation}</p>
          )}
          {ai.key_assumptions?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t('aiEngine.assumptions')}</p>
              {ai.key_assumptions.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400 mb-1">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-slate-300" />{a}
                </div>
              ))}
            </div>
          )}
          {ai.actionable_advice?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-violet-500 mb-2">{t('aiEngine.advice')}</p>
              {ai.actionable_advice.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300 mb-1.5">
                  <Target className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-violet-400" />{a}
                </div>
              ))}
            </div>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}
